import {
    afterNextRender,
    DestroyRef,
    Directive,
    ElementRef,
    inject,
    input,
    InputSignal,
    PLATFORM_ID,
    signal,
    WritableSignal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Reveals the host element with a fade/slide-up the first time it scrolls into view.
 *
 * SSR-safe: on the server the host stays fully visible (no hidden state is emitted), so content
 * remains crawlable and works without JS. The hidden initial state and the IntersectionObserver
 * are browser-only, applied after hydration.
 *
 * Set `[revealDisabled]="true"` to opt a host out entirely — useful for above-the-fold content
 * (e.g. the hero) that would otherwise flash hidden then animate in on first paint.
 */
@Directive({
    selector: '[appReveal]',
    host: {
        '[class.reveal]': 'isBrowser && !revealDisabled()',
        '[class.reveal--visible]': 'visible()',
    },
})
export class Reveal {
    private readonly _host: ElementRef<HTMLElement> = inject(ElementRef);
    private readonly _destroyRef: DestroyRef = inject(DestroyRef);
    protected readonly isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));
    protected readonly visible: WritableSignal<boolean> = signal(false);

    /** When `true`, the host renders without the reveal animation. */
    public readonly revealDisabled: InputSignal<boolean> = input(false);

    constructor() {
        // Browser-only and post-render by design: `IntersectionObserver` doesn't exist during SSR,
        // and the host must be laid out before we observe it — neither holds in `ngOnInit`.
        afterNextRender((): void => {
            if (this.revealDisabled()) {
                return;
            }

            const observer: IntersectionObserver = new IntersectionObserver(
                (entries: Array<IntersectionObserverEntry>): void => {
                    for (const entry of entries) {
                        // Reveal when scrolled into view, or if it's already above the viewport
                        // (e.g. after an in-page anchor jump) so content is never left hidden.
                        if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
                            this.visible.set(true);
                            observer.disconnect();
                            break;
                        }
                    }
                },
                { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
            );

            observer.observe(this._host.nativeElement);

            // Disconnect if the host is destroyed before it ever reveals (route change, list
            // re-render, structural `@if`), otherwise the observer leaks the element + callback.
            // `disconnect()` is idempotent, so the self-disconnect above stays safe too.
            this._destroyRef.onDestroy((): void => observer.disconnect());
        });
    }
}
